<template>
  <div dir="rtl" class="h-full bg-slate-50 md:flex md:items-center md:justify-center relative overflow-x-hidden font-sans text-slate-900 selection:bg-pink-200">

    <!-- Background Ambient Glows -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div class="absolute -top-40 -right-40 w-96 h-96 bg-pink-200/40 rounded-full blur-3xl opacity-70"></div>
      <div class="absolute top-40 -left-20 w-80 h-80 bg-purple-200/40 rounded-full blur-3xl opacity-70"></div>
    </div>

    <!-- Main Card Container -->
    <div class="relative z-10 w-full md:max-w-6xl h-full md:h-[800px] flex flex-col md:flex-row bg-white md:rounded-[2.5rem] md:shadow-2xl md:border border-slate-100 md:overflow-hidden">

      <!-- Right Side: Chat -->
      <div class="w-full md:w-1/2 flex flex-col bg-white h-full">

        <!-- Chat Header -->
        <div class="flex-none px-6 sm:px-10 pt-6 pb-4 bg-white border-b border-slate-100">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-9 h-9 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md shadow-pink-200">
                <Sparkles class="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 class="font-bold text-sm text-slate-900">הגדרת העסק עם AI</h1>
                <p class="text-xs text-slate-400">טיימאיט עוזרת לכם מההתחלה</p>
              </div>
            </div>
            <button
              class="text-slate-400 text-xs hover:text-slate-600 transition-colors px-3 py-1.5 rounded-lg hover:bg-slate-100"
              @click="skipSetup"
            >
              דילוג
            </button>
          </div>
        </div>

        <!-- Chat Body -->
        <div class="flex-1 overflow-hidden h-full">
          <OnboardingChat
            v-if="isReady"
            :businessName="businessName"
            :businessCategory="businessCategory"
            :businessSize="businessSize"
            :currentBookingMethod="currentBookingMethod"
            @skip="skipSetup"
          />
          <div v-else class="flex items-center justify-center h-full">
            <div class="flex gap-2">
              <span class="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style="animation-delay: 0s" />
              <span class="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style="animation-delay: 0.15s" />
              <span class="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style="animation-delay: 0.3s" />
            </div>
          </div>
        </div>

      </div>

      <!-- Left Side: AI Visual Panel (Desktop Only) -->
      <div class="hidden md:flex w-1/2 bg-gradient-to-br from-violet-50 via-purple-50 to-pink-50 p-12 flex-col justify-between relative overflow-hidden">

        <!-- Decorations -->
        <div class="absolute top-0 right-0 w-64 h-64 bg-purple-200/40 rounded-full blur-3xl"></div>
        <div class="absolute bottom-0 left-0 w-80 h-80 bg-pink-200/40 rounded-full blur-3xl"></div>

        <!-- Headline -->
        <div class="z-10 relative mt-4">
          <div class="inline-flex items-center gap-2 bg-white/70 border border-purple-100 rounded-full px-4 py-1.5 mb-5 shadow-sm">
            <Sparkles class="w-3.5 h-3.5 text-purple-500" />
            <span class="text-xs font-semibold text-purple-700">מופעל על ידי AI</span>
          </div>
          <h2 class="text-4xl lg:text-5xl font-black leading-tight mb-4 tracking-tight text-slate-900">
            3 שאלות קצרות,<br/>ואתם מוכנים להתחיל.
          </h2>
          <p class="text-slate-500 text-lg leading-relaxed max-w-sm">
            ה-AI שלנו מבין את העסק שלכם ועוזר לכם להגדיר הכל: שירותים, פיצ'רים, ואתר מקצועי.
          </p>
        </div>

        <!-- Step cards -->
        <div class="z-10 relative flex-1 flex flex-col justify-center space-y-3 mt-6">
          <div class="bg-white/80 border border-white rounded-2xl p-4 shadow-sm flex items-center gap-4">
            <div class="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Scissors class="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p class="font-bold text-sm text-slate-800">השירותים שלכם</p>
              <p class="text-xs text-slate-500 mt-0.5">העלו רשימה ונחלץ אותה אוטומטית</p>
            </div>
          </div>

          <div class="bg-white/80 border border-white rounded-2xl p-4 shadow-sm flex items-center gap-4 -translate-x-4">
            <div class="w-10 h-10 bg-pink-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Zap class="w-5 h-5 text-pink-600" />
            </div>
            <div>
              <p class="font-bold text-sm text-slate-800">פיצ'רים חכמים</p>
              <p class="text-xs text-slate-500 mt-0.5">AI בוחר מה הכי שימושי עבורכם</p>
            </div>
          </div>

          <div class="bg-white/80 border border-white rounded-2xl p-4 shadow-sm flex items-center gap-4 translate-x-3">
            <div class="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Globe class="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p class="font-bold text-sm text-slate-800">תוכן לאתר</p>
              <p class="text-xs text-slate-500 mt-0.5">כותרות ותיאורים מקצועיים</p>
            </div>
          </div>

          <div class="bg-white/80 border border-white rounded-2xl p-4 shadow-sm flex items-center gap-4">
            <div class="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <CalendarCheck class="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p class="font-bold text-sm text-slate-800">יומן מוכן לשימוש</p>
              <p class="text-xs text-slate-500 mt-0.5">תתחילו לקבל תורים מהיום הראשון</p>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Session from 'supertokens-web-js/recipe/session';
import {
  BusinessCategoryEnum,
  BusinessSizeEnum,
  CurrentBookingMethodEnum,
} from '@torim-app/types';
import { AdminRouteNames, AuthRouteNames } from '@app/router/route-names';
import { AdminApi } from '@app/api/admin/admin-api';
import { trackEvent } from '@app/composables/posthog';
import { Sparkles, Scissors, Zap, Globe, CalendarCheck } from 'lucide-vue-next';
import OnboardingChat from '@app/components/auth/onboarding/OnboardingChat.vue';

const router = useRouter();
const route = useRoute();

const isReady = ref(false);
const businessName = ref('');
const businessCategory = ref<BusinessCategoryEnum[]>([BusinessCategoryEnum.OTHER]);
const businessSize = ref<BusinessSizeEnum>(BusinessSizeEnum.SOLO);
const currentBookingMethod = ref<CurrentBookingMethodEnum>(CurrentBookingMethodEnum.NONE);

onMounted(async () => {
  const sessionExists = await Session.doesSessionExist();
  if (!sessionExists) {
    await router.replace({ name: AuthRouteNames.AdminLogin });
    return;
  }

  try {
    const redirectInfo = await AdminApi.getRedirect();
    if (redirectInfo.redirect === 'customer') {
      await router.replace({ name: AuthRouteNames.AdminLogin });
      return;
    }
  } catch {
    // Continue anyway
  }

  businessName.value = (route.query.businessName as string) || 'העסק שלך';
  const rawCat = route.query.businessCategory;
  if (Array.isArray(rawCat)) {
    businessCategory.value = rawCat as BusinessCategoryEnum[];
  } else if (rawCat) {
    businessCategory.value = (rawCat as string).split(',') as BusinessCategoryEnum[];
  } else {
    businessCategory.value = [BusinessCategoryEnum.OTHER];
  }
  businessSize.value = (route.query.businessSize as BusinessSizeEnum) || BusinessSizeEnum.SOLO;
  currentBookingMethod.value = (route.query.currentBookingMethod as CurrentBookingMethodEnum) || CurrentBookingMethodEnum.NONE;

  isReady.value = true;
});

const skipSetup = async () => {
  trackEvent('ai_onboarding_skipped');
  await AdminApi.updateState({ isFinishedOnBoarding: true });
  await router.replace({ name: AdminRouteNames.Home });
};
</script>
